/**
 * The existing room an indoor sauna (issue 156) is built into — issue 174/175.
 *
 * Draft state, stored on the design. Everything here is optional and absent
 * means "free-standing in the room, height above unknown": the defaults below
 * are what every indoor design saved before the field existed builds with.
 */
export const INDOOR_WALLS = ["front", "back", "left", "right"] as const;

export type IndoorWall = (typeof INDOOR_WALLS)[number];

export interface IndoorRoomConfig {
	/**
	 * Sauna walls built against a wall the room already has. They get a
	 * träfiberskiva and an air gap towards that wall instead of an outer panel
	 * (ISOVER, "Bygga bastu": "Bastukonstruktioner som byggs mot rummets
	 * konstruktioner ska ha träfiberskiva mellan luftspalten och reglarna").
	 */
	existingWalls: IndoorWall[];
	/**
	 * Sauna ceiling → the house's ridge, metres. Drives how much chimney a
	 * wood stove needs to pass through the rest of the house. `null` = not
	 * given: the chimney is quoted at `DEFAULT_HOUSE_ABOVE_CEILING_M` and says
	 * so.
	 */
	houseAboveCeilingM: number | null;
}

/**
 * Träfiberskiva on a sauna wall that stands against the room's wall (issue
 * 174): a 12 mm porous board, the common thickness sold for this.
 */
export const INDOOR_FIBREBOARD_T_M = 0.012;
/** Air gap between that board and the room's wall — ISOVER: "minst 20 mm". */
export const INDOOR_AIR_GAP_M = 0.02;

export const DEFAULT_INDOOR_ROOM: IndoorRoomConfig = {
	existingWalls: [],
	houseAboveCeilingM: null,
};

/**
 * Assumed when the height above is not given: one storey (2.4 m floor to
 * floor, the Swedish norm for villas) plus a 2.6 m roof — a 1½-plan house
 * with the sauna in the basement. Deliberately generous so an un-edited quote
 * does not under-buy pipe.
 */
export const DEFAULT_HOUSE_ABOVE_CEILING_M = 5;

export const HOUSE_ABOVE_CEILING_LIMITS = { min: 0.5, max: 15 } as const;

function isIndoorWall(value: unknown): value is IndoorWall {
	return (INDOOR_WALLS as readonly unknown[]).includes(value);
}

/** Canonical form: known walls only, no duplicates, in `INDOOR_WALLS` order. */
export function normalizeIndoorRoom(value: unknown): IndoorRoomConfig {
	if (!value || typeof value !== "object") return DEFAULT_INDOOR_ROOM;
	const raw = value as Partial<Record<keyof IndoorRoomConfig, unknown>>;
	const walls = Array.isArray(raw.existingWalls) ? raw.existingWalls : [];
	const existingWalls = INDOOR_WALLS.filter(
		(wall) => walls.includes(wall) && isIndoorWall(wall),
	);
	const height =
		typeof raw.houseAboveCeilingM === "number" &&
		Number.isFinite(raw.houseAboveCeilingM)
			? Math.min(
					HOUSE_ABOVE_CEILING_LIMITS.max,
					Math.max(
						HOUSE_ABOVE_CEILING_LIMITS.min,
						Math.round(raw.houseAboveCeilingM * 10) / 10,
					),
				)
			: null;
	return { existingWalls, houseAboveCeilingM: height };
}

export function freeWalls(room: IndoorRoomConfig): IndoorWall[] {
	return INDOOR_WALLS.filter((wall) => !room.existingWalls.includes(wall));
}

export function isDefaultIndoorRoom(room: IndoorRoomConfig): boolean {
	return room.existingWalls.length === 0 && room.houseAboveCeilingM === null;
}

/**
 * The stored form: `null` for "never edited" (absent, garbage, or exactly the
 * defaults), so a design that never touched the room saves nothing and an
 * old row and a new default row fingerprint the same.
 */
export function normalizeStoredIndoorRoom(
	value: unknown,
): IndoorRoomConfig | null {
	if (!value || typeof value !== "object") return null;
	const room = normalizeIndoorRoom(value);
	return isDefaultIndoorRoom(room) ? null : room;
}
